import axios from "axios";
import useAuth from "./useAuth";
import { UserId } from "../data_models/helpers";
import { TASK_INVITATION_ENDPOINTS } from "../config/apiEndpoints";
import { IApplication } from "../data_models/application";
import { Money } from "../data_models/money";
import { IInvitation, Invitation } from "../data_models/invitation";
import { ITask } from "../data_models/task";
import { IProject } from "../data_models/project";
import { FreelancerRole } from "../data_models/freelancer";



function useInvitations() {

    const { authToken } = useAuth();

    const newInvitation = async (freelancerId: string, taskId: string, projectId: string, payout?: number, description?: string): Promise<Invitation> => {
        try {
            const response = await axios.post(`${TASK_INVITATION_ENDPOINTS.POST_INVITATION}`,
                {
                    freelancerId,
                    taskId,
                    projectId,
                    payout,
                    description
                },
                {
                    headers: {
                        Authorization: `${authToken}`,
                    },
                });
            return response.data;
        } catch (error: any) {
            // Check if the error has a response and a data property
            if (error.response && error.response.data && error.response.data.error) {
                throw new Error(error.response.data.error); // Access the error message
            } else {
                throw new Error('An unexpected error occurred');
            }
        }
    }

    const newInvitationBulk = async (freelancerId: string, role: FreelancerRole, projectId: string, description?: string): Promise<Invitation> => {
        try {
            const response = await axios.post(`${TASK_INVITATION_ENDPOINTS.POST_INVITATION_BULK}`,
                {
                    freelancerId,
                    role,
                    projectId,
                    description
                },
                {
                    headers: {
                        Authorization: `${authToken}`,
                    },
                });
            return response.data;
        } catch (error: any) {
            // Check if the error has a response and a data property
            if (error.response && error.response.data && error.response.data.error) {
                throw new Error(error.response.data.error); // Access the error message
            } else {
                throw new Error('An unexpected error occurred');
            }
        }
    }

    const fetchInvitationById = async (invitationId: string): Promise<Invitation> => {
        try {
            const response = await axios.get(`${TASK_INVITATION_ENDPOINTS.GET_INVITATION_BY_ID}/${invitationId}`, {
                headers: {
                    Authorization: `${authToken}`,
                },
            });
            return response.data;
        } catch (error: any) {
            throw new Error('An unexpected error occurred');
        }
    }

    const fetchInvitationsForUser = async (page: number, limit: number, filter?: IInvitation): Promise<Invitation[]> => {
        try {
            const response = await axios.get(`${TASK_INVITATION_ENDPOINTS.GET_INVITATIONS_FOR_USER}`, {
                headers: {
                    Authorization: `${authToken}`,
                },
                params: {
                    filter: filter ? filter : {},
                    page: page,
                    limit: limit
                },
            });
            return response.data;
        } catch (error: any) {
            // Check if the error has a response and a data property
            if (error.response && error.response.data && error.response.data.error) {
                throw new Error(error.response.data.error); // Access the error message
            } else {
                throw new Error('An unexpected error occurred');
            }
        }
    }

    const fetchInvitationsFromUser = async (page: number, limit: number): Promise<Invitation[]> => {
        try {
            const response = await axios.get(`${TASK_INVITATION_ENDPOINTS.GET_INVITATIONS_FROM_USER}`, {
                headers: {
                    Authorization: `${authToken}`,
                },
                params: {
                    page: page,
                    limit: limit
                },
            });
            return response.data;
        } catch (error: any) {
            // Check if the error has a response and a data property
            if (error.response && error.response.data && error.response.data.error) {
                throw new Error(error.response.data.error); // Access the error message
            } else {
                throw new Error('An unexpected error occurred');
            }
        }
    }

    const fetchInvitationsForProject = async (projectId: string, page: number, limit: number): Promise<Invitation[]> => {
        try {
            const response = await axios.get(`${TASK_INVITATION_ENDPOINTS.GET_INVITATIONS_FOR_PROJECT}/${projectId}`, {
                headers: {
                    Authorization: `${authToken}`,
                },
                params: {
                    page: page,
                    limit: limit
                },
            });
            return response.data;
        } catch (error: any) {
            // Check if the error has a response and a data property
            console.log('error', error);
            if (error.response && error.response.data && error.response.data.error) {

                throw new Error(error.response.data.error); // Access the error message

            } else {
                throw new Error('An unexpected error occurred');
            }
        }
    }

    const fetchInvitationsForTask = async (taskId: string, page: number, limit: number): Promise<Invitation[]> => {
        try {
            const response = await axios.get(`${TASK_INVITATION_ENDPOINTS.GET_INVITATIONS_FOR_TASK}/${taskId}`, {
                headers: {
                    Authorization: `${authToken}`,
                },
                params: {
                    page: page,
                    limit: limit
                },
            });
            console.log('response', response.data);
            return response.data;
        } catch (error: any) {
            // Check if the error has a response and a data property
            if (error.response && error.response.data && error.response.data.error) {
                throw new Error(error.response.data.error); // Access the error message
            } else {
                throw new Error('An unexpected error occurred');
            }
        }
    }

    const fetchAliveInvitationsFromPm = async (page: number, limit: number): Promise<Invitation[]> => {
        try {
            const response = await axios.get(`${TASK_INVITATION_ENDPOINTS.GET_ALIVE_INVITATIONS_FROM_PM}`,
                {
                    headers: {
                        Authorization: `${authToken}`,
                    },
                    params: {
                        page: page,
                        limit: limit
                    }
                });
            return response.data;
        } catch (error: any) {
            // Check if the error has a response and a data property
            if (error.response && error.response.data && error.response.data.error) {
                throw new Error(error.response.data.error); // Access the error message
            } else {
                throw new Error('An unexpected error occurred');
            }
        }
    }

    const getNumberOfInvitationsForProject = async (projectId: string): Promise<number> => {
        try {
            const response = await axios.get(`${TASK_INVITATION_ENDPOINTS.GET_NUMBER_OF_INVITATIONS_FOR_PROJECT}/${projectId}`, {
                headers: {
                    Authorization: `${authToken}`,
                },
            });
            return response.data;
        } catch (error: any) {
            // Check if the error has a response and a data property
            if (error.response && error.response.data && error.response.data.error) {
                throw new Error(error.response.data.error); // Access the error message
            } else {
                throw new Error('An unexpected error occurred');
            }
        }
    }

    const acceptInvitation = async (invitationId: string): Promise<{ invitation: Invitation, task: ITask, project: IProject }> => {
        try {
            const response = await axios.put(`${TASK_INVITATION_ENDPOINTS.PUT_INVITATION_ACCEPT}/${invitationId}`,
                {},
                {
                    headers: {
                        Authorization: `${authToken}`,
                    },
                });
            return response.data;
        } catch (error: any) {
            // Check if the error has a response and a data property
            if (error.response && error.response.data && error.response.data.error) {
                throw new Error(error.response.data.error); // Access the error message
            } else {
                throw new Error('An unexpected error occurred');
            }
        }
    }

    const declineInvitation = async (invitationId: string): Promise<Invitation> => {
        try {
            const response = await axios.put(`${TASK_INVITATION_ENDPOINTS.PUT_INVITATION_DECLINE}/${invitationId}`,
                {},
                {
                    headers: {
                        Authorization: `${authToken}`,
                    },
                });
            return response.data;
        } catch (error: any) {
            // Check if the error has a response and a data property
            if (error.response && error.response.data && error.response.data.error) {
                throw new Error(error.response.data.error); // Access the error message
            } else {
                throw new Error('An unexpected error occurred');
            }
        }
    }

    const withdrawInvitation = async (invitationId: string): Promise<Invitation> => {
        try {
            const response = await axios.put(`${TASK_INVITATION_ENDPOINTS.PUT_INVITATION_WITHDRAW}/${invitationId}`,
                {},
                {
                    headers: {
                        Authorization: `${authToken}`,
                    },
                });
            return response.data;
        } catch (error: any) {
            // Check if the error has a response and a data property
            if (error.response && error.response.data && error.response.data.error) {
                throw new Error(error.response.data.error); // Access the error message
            } else {
                throw new Error('An unexpected error occurred');
            }
        }
    }

    const markInvitationAsSeenByFreelancer = async (invitationId: string) => {
        try {
            const response = await axios.put(`${TASK_INVITATION_ENDPOINTS.PUT_MARK_AS_SEEN_BY_FREELANCER}/${invitationId}`,
                {},
                {
                    headers: {
                        Authorization: `${authToken}`,
                    },
                });
            // no response data. updated invitation will be sent via sockets
        } catch (error: any) {
            // Check if the error has a response and a data property
            if (error.response && error.response.data && error.response.data.error) {
                throw new Error(error.response.data.error); // Access the error message
            } else {
                throw new Error('An unexpected error occurred');
            }
        }
    }

    return {
        newInvitation,
        fetchInvitationById,
        fetchInvitationsForUser,
        fetchInvitationsFromUser,
        fetchInvitationsForProject,
        fetchInvitationsForTask,
        fetchAliveInvitationsFromPm,
        getNumberOfInvitationsForProject,
        acceptInvitation,
        declineInvitation,
        withdrawInvitation,
        markInvitationAsSeenByFreelancer,
    };
}
export default useInvitations;